import { getWallets } from "@wallet-standard/app";
import type { Wallet, WalletAccount } from "@wallet-standard/base";
import {
  StandardConnect,
  StandardDisconnect,
  type StandardConnectFeature,
  type StandardDisconnectFeature,
} from "@wallet-standard/features";
import {
  SolanaSignIn,
  SolanaSignTransaction,
  type SolanaSignInFeature,
  type SolanaSignInInput,
  type SolanaSignTransactionFeature,
} from "@solana/wallet-standard-features";
import type { AuthResponse } from "@redchain/contracts";
import { api } from "../api/client";
import { solanaChain } from "../env";
import { bytesToBase64 } from "./tx";
import type { GameWallet } from "./types";

const LAST_WALLET_KEY = "redchain.externalWallet";

function supportsSolana(w: Wallet): boolean {
  return StandardConnect in w.features && SolanaSignTransaction in w.features && w.chains.some((c) => c.startsWith("solana:"));
}

/** Wallet Standard discovery (Phantom, Solflare, Backpack, …). */
export function listExternalWallets(): Wallet[] {
  return getWallets()
    .get()
    .filter(supportsSolana)
    .filter((w) => w.name !== "Privy");
}

export function onWalletsChanged(cb: () => void): () => void {
  const { on } = getWallets();
  const offRegister = on("register", cb);
  const offUnregister = on("unregister", cb);
  return () => {
    offRegister();
    offUnregister();
  };
}

export async function connectWallet(name: string, silent = false): Promise<{ wallet: Wallet; account: WalletAccount }> {
  const wallet = listExternalWallets().find((w) => w.name === name);
  if (!wallet) throw new Error(`${name} is not available in this browser`);
  const connect = (wallet.features as StandardConnectFeature)[StandardConnect];
  const { accounts } = await connect.connect(silent ? { silent: true } : undefined);
  const account = accounts.find((a) => a.chains.some((c) => c.startsWith("solana:"))) ?? accounts[0];
  if (!account) throw new Error(`${name} returned no Solana account`);
  localStorage.setItem(LAST_WALLET_KEY, name);
  return { wallet, account };
}

export function lastExternalWallet(): string | null {
  return localStorage.getItem(LAST_WALLET_KEY);
}

export async function disconnectWallet(wallet: Wallet): Promise<void> {
  localStorage.removeItem(LAST_WALLET_KEY);
  const f = (wallet.features as Partial<StandardDisconnectFeature>)[StandardDisconnect];
  await f?.disconnect().catch(() => {});
}

export type SiwsProof = Parameters<typeof api.authVerify>[0];

/** `solana:signIn` over the API-issued nonce (§20.1). */
export async function signInWithSolana(wallet: Wallet, account: WalletAccount): Promise<SiwsProof> {
  const feature = (wallet.features as Partial<SolanaSignInFeature>)[SolanaSignIn];
  if (!feature) throw new Error(`${wallet.name} doesn't support Sign In With Solana`);
  const n = await api.authNonce(account.address);
  const input: SolanaSignInInput = {
    domain: n.domain,
    address: account.address,
    statement: n.statement,
    nonce: n.nonce,
    issuedAt: n.issuedAt,
    expirationTime: n.expirationTime,
    ...(n.chainId ? { chainId: n.chainId } : {}),
  };
  const [out] = await feature.signIn(input);
  if (!out) throw new Error("Sign-in was cancelled");
  return {
    signInInput: input as unknown as Record<string, unknown>,
    signInOutput: {
      account: { address: out.account.address, publicKey: bytesToBase64(new Uint8Array(out.account.publicKey)) },
      signedMessage: bytesToBase64(new Uint8Array(out.signedMessage)),
      signature: bytesToBase64(new Uint8Array(out.signature)),
    },
  };
}

export function externalGameWallet(wallet: Wallet, account: WalletAccount): GameWallet {
  return {
    address: account.address,
    kind: "external",
    async signIn(): Promise<AuthResponse> {
      const proof = await signInWithSolana(wallet, account);
      const res = await api.authVerify(proof);
      api.setToken(res.accessToken);
      return res;
    },
    async signTransaction(bytes: Uint8Array): Promise<Uint8Array> {
      const feature = (wallet.features as SolanaSignTransactionFeature)[SolanaSignTransaction];
      const chain = account.chains.includes(solanaChain as `${string}:${string}`) ? (solanaChain as `${string}:${string}`) : undefined;
      const [out] = await feature.signTransaction({ account, transaction: bytes, ...(chain ? { chain } : {}) });
      if (!out) throw new Error("Signing was cancelled");
      return new Uint8Array(out.signedTransaction);
    },
  };
}
